import { createRef, useMemo, useState } from "react";

import useUpdatedEffect from "app/hooks/useUpdatedEffect";
import tw from "twin.macro";

type PinValue = string[];

interface Props {
  onPinCompleted: (code: string) => void;
  disabled?: boolean;
  pinLength?: number;
  className?: string;
  error?: boolean;
  errorMessage?: string;
}

export default function PinInput(props: Props) {
  const {
    disabled,
    error,
    errorMessage,
    pinLength = 6,
    onPinCompleted,
  } = props;

  const [values, setValues] = useState<PinValue>(Array(pinLength).fill(""));

  const inputRefs = useMemo(
    () =>
      Array(pinLength)
        .fill(null)
        .map(() => createRef<HTMLInputElement>()),
    [pinLength]
  );

  const handleChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 1);

      setValues((prev) => {
        const next = [...prev];
        next[index] = value;
        return next;
      });

      if (value) {
        focusInput(index + 1);
      }
    };

  const handleKeyDown =
    (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case "Backspace":
          if (!values[index]) {
            focusInput(index - 1);
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          focusInput(index - 1);
          break;
        case "ArrowRight":
          e.preventDefault();
          focusInput(index + 1);
          break;
        default:
          break;
      }
    };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (disabled) return;

    const pastedData = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    const pastedArray = pastedData.split("").slice(0, pinLength);

    const newValues = Array(pinLength).fill("");
    pastedArray.forEach((value, index) => {
      newValues[index] = value;
    });

    setValues(newValues);

    const nextEmptyIndex = newValues.findIndex((value) => !value);
    focusInput(nextEmptyIndex >= 0 ? nextEmptyIndex : pinLength - 1);
  };

  const focusInput = (index: number) => {
    if (index < 0 || index >= pinLength) return;
    inputRefs[index].current?.focus();
  };

  useUpdatedEffect(() => {
    const code = values.join("");
    if (code.length === pinLength) {
      onPinCompleted(code);
    }
  }, [values]);

  return (
    <div>
      <div
        tw="flex items-center gap-[8px]"
        role="group"
        aria-label="PIN input"
      >
        {values.map((value, index) => (
          <input
            key={index}
            ref={inputRefs[index]}
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            autoComplete="off"
            value={value}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste}
            autoFocus={index === 0}
            disabled={disabled}
            aria-label={`PIN digit ${index + 1}`}
            css={[
              tw`w-[53px] h-[56px] text-[16px] font-semibold px-[16px] py-[8px] border border-border-mid bg-input-surface text-center rounded-[10px] outline-none focus:border-2 focus:border-border-higher disabled:opacity-50 disabled:cursor-not-allowed`,
              error && tw`border-border-error`,
            ]}
          />
        ))}
      </div>

      {errorMessage && (
        <p tw="text-[16px] text-button-danger mt-[8px]" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
